var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from 'react';
export function usePolling(callback, options = {}) {
    const { deps = [], interval = 4000, onError, startImmediately = true } = options;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [pollCount, setPollCount] = useState(0);
    const timeoutId = useRef();
    const isPollingRef = useRef(false);
    const isCancelledRef = useRef(false);
    const currentIntervalRef = useRef(interval);
    // Update interval ref when it changes
    currentIntervalRef.current = interval;
    const onErrorCallback = useCallback(onError || (() => { }), [onError]);
    const memoizedCallback = useCallback(callback, deps);
    const startPolling = useCallback(() => {
        if (isCancelledRef.current || isPollingRef.current) {
            return;
        }
        const pollData = () => __awaiter(this, void 0, void 0, function* () {
            if (isCancelledRef.current || isPollingRef.current) {
                return;
            }
            isPollingRef.current = true;
            setLoading(true);
            try {
                const result = yield memoizedCallback();
                if (!isCancelledRef.current) {
                    setData(result);
                    setError(null);
                }
            }
            catch (err) {
                if (!isCancelledRef.current && err instanceof Error) {
                    setError(err);
                    if (onErrorCallback) {
                        onErrorCallback(err);
                    }
                }
            }
            finally {
                isPollingRef.current = false;
                if (!isCancelledRef.current) {
                    setPollCount((current) => current + 1);
                    setLoading(false);
                    // Schedule the next poll only after this one completes
                    timeoutId.current = setTimeout(() => {
                        if (!isCancelledRef.current) {
                            startPolling();
                        }
                    }, currentIntervalRef.current);
                }
            }
        });
        pollData();
    }, [memoizedCallback, onErrorCallback]);
    useEffect(() => {
        isCancelledRef.current = false;
        if (startImmediately) {
            startPolling();
        }
        else {
            timeoutId.current = setTimeout(() => {
                if (!isCancelledRef.current) {
                    startPolling();
                }
            }, currentIntervalRef.current);
        }
        return () => {
            isCancelledRef.current = true;
            if (timeoutId.current) {
                clearTimeout(timeoutId.current);
                timeoutId.current = undefined;
            }
            isPollingRef.current = false;
        };
    }, [startPolling]);
    const cancel = useCallback(() => {
        isCancelledRef.current = true;
        if (timeoutId.current) {
            clearTimeout(timeoutId.current);
            timeoutId.current = undefined;
        }
        isPollingRef.current = false;
    }, []);
    return { cancel, data, error, loading, pollCount };
}
